export enum EConversionSIToImperial {
    m_to_ft = 3.28083,
    mm_to_in = 0.03937,

    m2_to_ft2 = 10.763845,
    mm2_to_in2 = 0.00155,
    m3_to_ft3 = 35.314347,
    kN_to_lbf = 224.8089,
    kNm_to_lbfft = 68.521959,

    kN_m_to_lbf_ft = 737.56,

    Nm2_to_lbfft2 = 0.020885,
    kNm3_to_lbfft3 = 6.242795,
    kgm2_to_lbfft2 = 0.028783,

    m_to_in = 39.3701,
    m3s_to_ft3s = 35.3147,
    ms_to_fts = 3.28084,
    Nm3_to_lbft3 = 6.4231142857,
    m3sm_to_ft3sft = 10.7639,

    MPa_to_lbfft2 = 0.020885,

    MPa_to_psi = 145.03773800722,
    MPa_to_ksi = 0.1450377377,
    kmh_to_mph = 0.621371,
}

export enum EConversionImperialToSI {
    ft_to_m = 1 / 3.28084,
    in_to_mm = 1 / 0.0393701,
    in_to_m = 1 / 39.3701,
    ft2_to_m2 = 1 / 10.7639,
    in2_to_mm2 = 1 / 0.00155,
    ft3_to_m3 = 1 / 35.3147,
    lbf_to_kN = 1 / 224.809,
    lbfft_to_kNm = 1 / 68.5218,
    lbf_ft_to_kN_m = 1 / 737.56,
    lbfft2_to_Nm2 = 1 / 0.0208854,
    lbfft3_to_kNm3 = 1 / 6.36588,
    ft3s_to_m3s = 1 / 35.3147,
    fts_to_ms = 1 / 3.28084,
    lbft3_to_Nm3 = 1 / 6.4231142857,
    ft3sft_to_m3sm = 1 / 10.7639,
    mph_to_kmh = 1 / 0.621371,
}

// Oggetto RIVISTO con abbreviazioni standard per 'convert-units'
export const convertUnits = {
    // Lunghezza
    m_ft: ['m', 'ft'] as const,
    mm_in: ['mm', 'in'] as const,
    // Area
    m2_ft2: ['m2', 'ft2'] as const,
    mm2_in2: ['mm2', 'in2'] as const,
    // Volume
    m3_ft3: ['m3', 'ft3'] as const,
    // Forza
    kN_lbf: ['kN', 'lbf'] as const,
    // Forza per Lunghezza (es. carichi lineari)
    kNm_lbfft: ['kN/m', 'lbf/ft'] as const,
    // Pressione / Stress
    kPa: ['kPa', 'psi'] as const, // Kilopascal <-> Pound per Square Inch
    kPa_psf: ['kPa', 'psf'] as const, // Kilopascal <-> Pound per Square Foot
    MPa_psi: ['MPa', 'psi'] as const,
    MPa_ksi: ['MPa', 'ksi'] as const, // MegaPascal <-> Kip per Square Inch
    // Densità (Massa / Volume)
    kgm3_lbfft3: ['kg/m3', 'lb/ft3'] as const,
    // Peso Specifico (Forza / Volume)
    kNm3_pcf: ['kN/m3', 'lbf/ft3'] as const, // kN/m³ <-> Pound-force per cubic foot (pcf)
    Nm3_lbfft3: ['N/m3', 'lbf/ft3'] as const, // N/m³  <-> pcf
    // Portata Volumetrica
    m3s_ft3s: ['m3/s', 'ft3/s'] as const, // Metri cubi/sec <-> Piedi cubi/sec
    // Velocità
    ms_fts: ['m/s', 'ft/s'] as const,
    kmh_mph: ['km/h', 'mph'] as const,
    // Massa per Area (es. geomembrane)
    kgm2_lbfft2: ['kg/m2', 'lb/ft2'] as const, // Verificare se lb/ft2 è standard US per questo o se serve oz/yd2
    // Angolo
    deg: ['deg', 'deg'] as const, // Gradi (la libreria usa 'deg')
    rad: ['rad', 'rad'] as const, // Radianti
    // Tempo
    years: ['year', 'year'] as const, // La libreria usa 'year' singolare
    // Adimensionali / Non Convertibili
    percent: ['%', '%'] as const, // Percentuale
    unitless: ['', ''] as const, // Per coefficienti, fattori di sicurezza etc.
    m_m_ft_ft: ['m/m', 'ft/ft'] as const, // Pendenza (adimensionale)

    // ----- Unità che richiedono gestione MANUALE (non direttamente in convert-units) -----
    // Velocità di deformazione (es. consolidamento)
    mm_y_in_y: ['mm', 'in'] as const, // Converti solo lunghezza, il '/year' è uguale
    // Permeabilità / Portata per unità di larghezza
    m3_s_m_ft3_s_ft: ['m3/s', 'ft3/s'] as const, // Converti solo m3/s -> ft3/s, poi dividi per m o ft
    // Momento Flettente
    kNm_lbfft_moment: ['kN', 'lbf', 'm', 'ft'] as const, // Salva unità base forza/lunghezza, calcola M=F*L
};

// Tipo helper (opzionale)
export type ConversionUnitKey = keyof typeof convertUnits;

export const conversionLabel = {
    m_ft: ['m', 'ft'],
    mm_ftft: ['m/m', 'ft/ft'],
    mm_in: ['mm', 'in'],
    mmy_iny: ['mm/y', 'in/y'],
    m_in: ['m', 'in'],
    kmh_mph: ['km/h', 'mph'],
    m2_ft2: ['m²', 'ft²'],
    mm2_in2: ['mm²', 'in²'],
    m3_ft3: ['m³', 'ft³'],
    kN_lbf: ['kN', 'lbf'],
    kNm_lbfft: ['kN/m', 'lbf/ft'],
    kN_m_lbf_ft: ['kN*m', 'lbf*ft'],
    Nm2_lbfft2: ['N/m²', 'lbf/ft²'],
    kNm3_lbfft3: ['kN/m³', 'lbf/ft³'],
    m3s_ft3s: ['m³/s', 'ft³/s'],
    ms_fts: ['m/s', 'ft/s'],
    Nm3_lbfft3: ['N/m³', 'lbf/ft³'],
    m3sm_ft3sft: ['m³/s/m', 'ft³/s/ft'],
    kgm2_lbfft2: ['kg/m²', 'lb/ft²'],
    kgm3_lbfft3: ['kg/m³', 'lb/ft³'],
    deg: ['°', '°'],
    mpa: ['MPa', 'lbf/ft²'],
    mpa_mpa: ['MPa', 'MPa'],
    mpa_psi: ['MPa', 'Psi'],
    mpa_ksi: ['MPa', 'ksi'],
    years: ['years', 'years'],
    kPa: ['kPa', 'psi'],
    kPa_psf: ['kPa', 'psf'],
};

export const conversionLabelNEW = {
    meter: 'm',
    feet: 'ft',
    millimeter: 'mm',
    inch: 'in',
    millimeter_per_year: 'mm/y',
    meter_per_year: 'm/y',
    meter_per_second: 'm/s',
    feet_per_second: 'ft/s',
    meter_per_second_squared: 'm/s²',
    feet_per_second_squared: 'ft/s²',
    meter_per_second_cubed: 'm/s³',
    feet_per_second_cubed: 'ft/s³',
    square_meter: 'm²',
    square_feet: 'ft²',
    square_millimeter: 'mm²',
    square_inch: 'in²',
    cubic_meter: 'm³',
    cubic_feet: 'ft³',
    cubic_millimeter: 'mm³',
    cubic_inch: 'in³',
    kilonewton: 'kN',
    pound_force: 'lbf',
    kilonewton_per_meter: 'kN/m',
    pound_force_per_foot: 'lbf/ft',
    kilonewton_per_cubic_meter: 'kN/m³',
    pound_force_per_cubic_foot: 'lbf/ft³',
    newton_per_square_meter: 'N/m²',
    pound_force_per_square_foot: 'lbf/ft²',
    newton_per_cubic_meter: 'N/m³',
    newton_per_square_centimeter: 'N/cm²',
};

export const convertTo = (value: number, type: EConversionSIToImperial | EConversionImperialToSI): number => {
    return value * type;
};
